/**
 * Structured blur ⇄ CSS filter helpers. Layer blur lives in `filter: blur()` and background blur in
 * `backdrop-filter: blur()`. The element keeps the preserved generation `filter` / `backdropFilter` strings
 * (which may carry NON-blur filters like `brightness()` too), so composing has to splice the structured
 * blur in while leaving any sibling filters intact.
 */

const BLUR_RE = /blur\(\s*([0-9.]+)px\s*\)/i

/** The first `blur(Npx)` radius found in a CSS filter string, or undefined. Used to seed the panel from a
 *  generated element that only has the raw string (mirrors `cssToShadows` seeding from `boxShadow`). */
export function cssBlurAmount(css: string | undefined | null): number | undefined {
  if (!css) return undefined
  const m = BLUR_RE.exec(css)
  return m ? Number(m[1]) : undefined
}

/** A CSS filter string with any `blur()` term removed (and whitespace tidied), or undefined when nothing
 *  remains. Lets the structured blur replace just the blur term while preserving e.g. `brightness(1.1)`. */
export function stripBlur(css: string | undefined | null): string | undefined {
  if (!css) return undefined
  const rest = css.replace(BLUR_RE, ' ').replace(/\s+/g, ' ').trim()
  return rest.length ? rest : undefined
}

/**
 * Compose a final CSS filter value from a preserved string and a structured blur radius. Two distinct
 * cases — conflating them silently drops blur on old/generated decks:
 *  - blur == null  → the blur is UNTOUCHED (no structured field yet, e.g. a freshly-baked or pre-existing
 *    deck whose blur still lives in the preserved string). Keep the string verbatim.
 *  - blur is a number → the structured field is authoritative: strip any blur from the preserved string
 *    and append `blur(${blur}px)` when > 0 (0 = explicitly removed → no blur term).
 */
export function composeFilter(preserved: string | undefined, blur: number | undefined): string | undefined {
  if (blur == null || Number.isNaN(blur)) return preserved || undefined
  const base = stripBlur(preserved)
  const blurTerm = blur > 0 ? `blur(${blur}px)` : undefined
  return [base, blurTerm].filter(Boolean).join(' ') || undefined
}
